import React, { ReactNode } from 'react'
import type { FieldError as HookFieldError } from 'react-hook-form'
import { cn } from '@/lib/utils'
import Field from '@/components/Field'
import { IconTypes } from '@/components/Icon'
import FieldError from '@/components/livecontact/FieldError'
import { LABEL_CLASS, NOTE_CLASS } from '@/components/livecontact/fieldStyles'

type DateFieldOverrides = {
  classInput?: string
  placeholder?: string
  icon?: IconTypes
  disabled?: boolean
  readOnly?: boolean
  classInnerInput?: string
  className?: string
}

type Props = {
  className?: string
  classInput?: string
  classLabel?: string
  classLabelInner?: string
  classInnerInput?: string
  label?: ReactNode
  note?: string
  value: [string, string] | []
  onChange: (value: [string, string] | []) => void
  placeholderFrom?: string
  placeholderTo?: string
  required?: boolean
  disabled?: boolean
  error?: HookFieldError
  icon?: IconTypes
  classIcon?: string
  readOnly?: boolean
  startDateProps?: DateFieldOverrides
  endDateProps?: DateFieldOverrides
}

/** Two date fields joined by an en dash; both share the base Field surface. */
const DateRangeField = ({
  className,
  classInput,
  classLabel,
  classLabelInner,
  classInnerInput,
  label,
  note,
  value,
  onChange,
  placeholderFrom = 'From date',
  placeholderTo = 'To date',
  required,
  disabled,
  error,
  icon,
  classIcon,
  readOnly = false,
  startDateProps,
  endDateProps,
}: Props) => {
  const getRange = (): [string, string] => (value.length === 2 ? value : ['', ''])

  const handleFromChange = (val: string) => {
    const [, to] = getRange()
    onChange([val, to])
  }

  const handleToChange = (val: string) => {
    const [from] = getRange()
    onChange([from, val])
  }

  return (
    <div className={cn(className)}>
      {label && (
        <div className={cn(LABEL_CLASS, classLabelInner)}>
          <span className={classLabel}>{label}</span>
        </div>
      )}
      <div className={cn('flex items-center gap-2', classInnerInput)}>
        <Field
          type="date"
          value={value[0] ?? ''}
          onChange={handleFromChange}
          placeholder={startDateProps?.placeholder || placeholderFrom}
          required={required}
          disabled={startDateProps?.disabled || disabled}
          readOnly={startDateProps?.readOnly || readOnly}
          icon={startDateProps?.icon || icon}
          classInput={startDateProps?.classInput || classInput}
          classIcon={classIcon}
          classInnerInput={startDateProps?.classInnerInput}
          className={cn('min-w-0 flex-1', startDateProps?.className)}
          showError={false}
        />
        <span className="shrink-0 text-[13px] text-ink-4" aria-hidden>
          –
        </span>
        <Field
          type="date"
          value={value[1] ?? ''}
          onChange={handleToChange}
          placeholder={endDateProps?.placeholder || placeholderTo}
          required={required}
          disabled={endDateProps?.disabled || disabled}
          readOnly={endDateProps?.readOnly || readOnly}
          icon={endDateProps?.icon || icon}
          classInput={endDateProps?.classInput || classInput}
          classIcon={classIcon}
          classInnerInput={endDateProps?.classInnerInput}
          className={cn('min-w-0 flex-1', endDateProps?.className)}
          showError={false}
        />
      </div>
      {note && <p className={NOTE_CLASS}>{note}</p>}
      <FieldError message={error?.message} />
    </div>
  )
}

export default DateRangeField
